package com.crowpay.views.components.nav

import com.crowpay.sdk.currentSession
import com.crowpay.views.dialogs.GenericDialog
import com.crowpay.views.screens.auth.AuthScreen
import com.crowpay.views.screens.common.LandingScreen
import com.crowpay.views.screens.common.UnauthorizedLensException
import com.crowpay.views.theming.MobileNavSemantic
import com.crowpay.views.theming.greySeparator
import com.crowpay.views.theming.withoutBack
import com.lightningkite.kiteui.exceptions.ExceptionHandler
import com.lightningkite.kiteui.models.MainContentSemantic
import com.lightningkite.kiteui.models.dp
import com.lightningkite.kiteui.models.px
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.ScreenNavigator
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.mainScreenNavigator
import com.lightningkite.kiteui.navigation.screenNavigator
import com.lightningkite.kiteui.reactive.AppState
import com.lightningkite.kiteui.reactive.awaitOnce
import com.lightningkite.kiteui.reactive.reactive
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.views.RView
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.bar
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.padded
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.direct.stack
import com.lightningkite.kiteui.views.direct.swapView
import com.lightningkite.kiteui.views.direct.swapping
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.l2.navigatorViewDialog
import com.lightningkite.kiteui.views.l2.overlayStack
import com.lightningkite.kiteui.views.mainContent
import com.lightningkite.kiteui.views.nav
import com.lightningkite.kiteui.views.rContextAddonGenerate
import com.lightningkite.kiteui.views.rContextAddonInit
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.time.Duration.Companion.seconds

val ViewWriter.navExists by rContextAddonGenerate {
    shared { currentSession() != null && mainScreenNavigator.currentScreen() !is AuthScreen }
}

val windowIsNarrow = shared(useLastWhileLoading = true) { AppState.windowInfo().width < CrowPayDimensions.rerenderWidth }

var ViewWriter.viewIsNarrow by rContextAddonInit<Boolean>()
    private set

val ViewWriter.viewIsWide get() = !viewIsNarrow

fun ViewWriter.navigatorBasedOnPageWidth() = if (viewIsNarrow) mainScreenNavigator else dialogScreenNavigator

fun ViewWriter.crowpay(): ViewModifiable =
    stack {
        overlayStack = this
        spacing = 0.dp

        this += object : ExceptionHandler {
            override val priority: Float = 5f
            override fun handle(view: RView, working: Boolean, exception: Exception): (() -> Unit)? {
                if (exception !is UnauthorizedLensException) return null

                var dismissed = false

                suspend fun ViewWriter.dismiss() {
                    if (dismissed) return
                    mainScreenNavigator.run { if (canGoBack.awaitOnce()) goBack() else reset(LandingScreen()) }
                    dialogScreenNavigator.dismiss()
                    dismissed = true
                }

                view.dialogScreenNavigator.navigate(
                    GenericDialog(
                        "Unauthorized",
                        """
                            ${exception.message ?: "You are not allowed to view this page"},
                            This page will automatically close in 5 seconds.
                        """.trimIndent(),
                        dismissText = "Go Back",
                        dismiss = ViewWriter::dismiss
                    )
                )

                view.launch {
                    delay(5.seconds)
                    dismiss()
                }

                return { }
            }
        }

        stack {
            spacing = 0.dp
            reactive {
                println("rerendering crowpay")
                clearChildren()
                if (windowIsNarrow()) {
                    viewIsNarrow = true
                    crowpayNarrow()
                }
                else {
                    viewIsNarrow = false
                    crowpayWide()
                }
            }
        }

        navigatorViewDialog()
    }

private fun ViewWriter.crowpayWide() =
    col {
        spacing = theme.navSpacing

        bar - crowpayNavHeaderWide()

        expanding - row {
            spacing = theme.navSpacing

            nav - sizeConstraints(minWidth = 12.rem) - crowpaySideNav()

            expanding - crowpayNavigatorView(mainScreenNavigator)
        }
    }

private fun ViewWriter.crowpayNarrow() =
    MobileNavSemantic.onNext - col {
        spacing = theme.navSpacing

        bar - crowpayNavHeaderNarrow()

        expanding - crowpayNavigatorView(mainScreenNavigator)

        greySeparator()
        space(0.5)

        nav - crowpayBottomNav()

        space(0.5)
    }

private fun ViewWriter.crowpayNavigatorView(navigator: ScreenNavigator): ViewModifiable {
    return swapView {
        var lastStack = navigator.stack.value
        swapping(
            transition = {
                val newStack = navigator.stack.value
                val transitionSet = theme.bodyTransitions
                when {
                    newStack.size - lastStack.size > 0 -> transitionSet.forward
                    newStack.size - lastStack.size < 0 && newStack.firstOrNull() == lastStack.firstOrNull() -> transitionSet.reverse
                    else -> transitionSet.neutral
                }.also { lastStack = newStack }
            },
            current = { navigator.currentScreen<Page?>() to (currentSession() != null) },
            views = { (screen, loggedIn) ->
                with(split()) {
                    this.screenNavigator = navigator
                    if (screen != null)
                        with(screen) {
                            stack {
                                spacing = AppDimensions.fullIndent
                                forcePadding = loggedIn && screen !is AuthScreen
                                render2()
                            }
                        }
                }
            }
        )
    }
}